import { formatTimestamp } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { BridgeStatus, WizardState } from "@/types/wizard";

/** True when the operator must run Apply (first start or config drift). */
export function isBootstrapWorkPending(
  state: WizardState | undefined,
  status: BridgeStatus | undefined,
): boolean {
  if (state) {
    return state.firstStartPending || state.updatePending;
  }

  return !!(status?.firstStartPending || status?.updatePending);
}

export type WizardVerdictKind = "apply-required" | "system-ok";

export interface WizardVerdict {
  kind: WizardVerdictKind;
  headline: string;
  summary: string;
  detail?: string;
}

export function getWizardVerdict(
  state: WizardState | undefined,
  status: BridgeStatus | undefined,
): WizardVerdict {
  const lastApplied = formatTimestamp(state?.lastAppliedAt ?? status?.lastBootstrapCompletedAt);
  const operator = state?.lastOperator?.trim();

  if (state?.firstStartPending || status?.firstStartPending) {
    return {
      kind: "apply-required",
      headline: strings.verdict.applyRequiredHeadline,
      summary: strings.verdict.firstInstallSummary,
      detail: strings.verdict.firstInstallDetail,
    };
  }

  if (state?.updatePending || status?.updatePending) {
    return {
      kind: "apply-required",
      headline: strings.verdict.applyRequiredHeadline,
      summary: strings.verdict.configUpdateSummary,
      detail: state?.pendingReason ?? status?.pendingReason ?? strings.verdict.configUpdateDetail,
    };
  }

  const lastAppliedLine =
    lastApplied !== "—"
      ? operator
        ? strings.verdict.lastAppliedWithOperator(lastApplied, operator)
        : strings.verdict.lastApplied(lastApplied)
      : null;

  const wizardModeNote = state?.wizardRequiredMode
    ? strings.verdict.wizardModeNote
    : null;

  return {
    kind: "system-ok",
    headline: strings.verdict.systemOkHeadline,
    summary: strings.verdict.systemOkSummary,
    detail: [lastAppliedLine, wizardModeNote].filter(Boolean).join(" ") || undefined,
  };
}
